import React, { useState } from "react";
import { connect } from "react-redux";
import { Link, useLocation } from "react-router-dom";

import {
  PlusCircle,
  Trash2,
  Edit2,
  Search as SearchIcon,
  AlertTriangle,
  DoorOpen,
  MapPin,
  Building2,
  ChevronRight
} from "lucide-react";
import {
  getAllDoors,
  setDoor,
  deleteDoor,
} from "../../store/actions";
import {
  Button,
  Table,
  Breadcrumb,
  Modal,
  useToast,
  Card,
  Badge
} from "../UI";
import axios from "axios";
import { useTableSearch } from "../Common/useTableSearch";



const DoorList = ({
  setDoor,
  deleteDoor,
  doors,
  singleDoor,
}: any) => {
  const { toast } = useToast();
  const [searchVal, setSearchVal] = useState<any>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [doorToDelete, setDoorToDelete] = useState<any>(null);

  const [refreshToggle, setRefreshToggle] = useState(0);

  const fetchDoors = React.useCallback(async () => {
    const { data } = await axios.get(`api/doors/getalldoors`);
    return { data };
  }, [refreshToggle]);

  const { filteredData, loading } = useTableSearch({
    searchVal,
    retrieve: fetchDoors,
  });
  const { search } = useLocation();
  const siteIdParam = new URLSearchParams(search).get('siteId');
  const displayedData: any[] = siteIdParam
    ? filteredData.filter((d: any) => {
      const site = d.site || {};
      return site.id === siteIdParam || site._id === siteIdParam;
    })
    : filteredData;

  const handleDelete = async () => {
    if (doorToDelete) {
      try {
        await deleteDoor(doorToDelete.id);
        setRefreshToggle(prev => prev + 1);
        toast("Door deleted successfully", "success");
        setIsDeleteModalOpen(false);
        setDoorToDelete(null);
      } catch (error) {
        toast("Failed to delete door", "error");
      }
    }
  };

  const columns = [
    {
      title: "Door Name / Location",
      key: "alloweddoor",
      width: "40%",
      render: (val: any) => (
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-[#8dc63f]/15 flex items-center justify-center shrink-0">
            <DoorOpen className="w-4 h-4 text-[#8dc63f]" />
          </div>
          <span className="font-medium text-xs md:text-sm text-slate-800 tracking-tight">{val}</span>
        </div>
      )
    },
    {
      title: "Associated Site",
      key: "site",
      width: "35%",
      render: (val: any, record: any) => (
        <div className="flex items-center gap-2 text-slate-600 font-medium text-xs md:text-sm">
          <Building2 className="w-3.5 h-3.5 text-slate-400 shrink-0" />
          <span className="truncate" title={record.site?.sitename}>{record.site?.sitename || "Unknown Site"}</span>
        </div>
      )
    },
    {
      title: "Actions",
      key: "actions",
      width: "25%",
      render: (_: any, record: any) => (
        <div className="flex items-center gap-1.5">
          <Link to="/editdoor" onClick={(e) => e.stopPropagation()}>
            <Button
              variant="ghost"
              size="sm"
              className="h-9 w-9 p-0 text-slate-400 hover:text-[#8dc63f] hover:bg-[#8dc63f]/10 transition-colors flex items-center justify-center border-none bg-transparent rounded-lg"
              onClick={() => setDoor(record)}
            >
              <Edit2 className="w-4 h-4 text-[#8dc63f]" />
            </Button>
          </Link>
          <Button
            variant="ghost"
            size="sm"
            className="h-9 w-9 p-0 text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-colors flex items-center justify-center border-none bg-transparent rounded-lg"
            onClick={(e) => { e.stopPropagation(); setDoorToDelete(record); setIsDeleteModalOpen(true); }}
          >
            <Trash2 className="w-4 h-4 text-rose-600" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-2">
        <Breadcrumb
          items={[
            { label: 'Home', to: '/dashboard' },
            { label: 'Doors' }
          ]}
        />
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-2.5 flex-wrap">
            <div className="w-9 h-9 bg-[#8dc63f]/15 rounded-xl flex items-center justify-center">
              <DoorOpen className="w-4 h-4 text-[#8dc63f]" />
            </div>
            <h1 className="text-xl font-bold text-slate-900 tracking-tight leading-none">Doors</h1>
            {displayedData.length > 0 && (
              <span className="text-xs font-semibold text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">
                {displayedData.length}
              </span>
            )}
            {siteIdParam && displayedData.length > 0 && (
              <span className="text-xs font-medium text-[#8dc63f] bg-[#8dc63f]/10 px-2.5 py-0.5 rounded-full flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                {displayedData[0]?.site?.sitename || 'Site filter active'}
              </span>
            )}
          </div>

          <div className="flex items-center gap-2">
            <div className="relative group">
              <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 group-focus-within:text-[#8dc63f] transition-colors" />
              <input
                type="text"
                placeholder="Search doors..."
                onChange={(e) => setSearchVal(e.target.value)}
                className="pl-9 pr-3 h-8 bg-slate-50 border border-slate-100 rounded-lg text-xs font-normal focus:bg-white focus:border-[#8dc63f] focus:ring-2 focus:ring-[#8dc63f]/5 transition-all outline-none w-52"
              />
            </div>

            <Link to="/adddoor">
              <Button className="h-10 px-3 gap-1.5 text-xs font-semibold bg-[#8dc63f] hover:bg-[#76ab32] shadow-sm">
                <PlusCircle className="w-3.5 h-3.5" />
                Add Door
              </Button>
            </Link>
          </div>
        </div>
      </div>


      <Table
        className="max-w-5xl"
        columns={columns}
        dataSource={displayedData}
        rowKey="id"
        onRowClick={(record) => {
          setDoor(record);
        }}
      />


      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title="Confirm Deletion"
        footer={
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={() => setIsDeleteModalOpen(false)}>Cancel</Button>
            <Button variant="danger" onClick={handleDelete} className="gap-2">
              <Trash2 className="w-4 h-4" />
              Delete Door
            </Button>
          </div>
        }
      >
        <div className="flex gap-4">
          <div className="p-3 bg-rose-100 rounded-2xl h-fit">
            <AlertTriangle className="w-6 h-6 text-rose-600" />
          </div>
          <div className="space-y-2">
            <p className="text-slate-900 font-bold">Are you sure you want to delete this door?</p>
            <p className="text-sm text-slate-500 font-medium leading-relaxed">
              Deleting <span className="text-slate-900 font-black">{doorToDelete?.alloweddoor}</span> will remove it from all associated request forms. This action cannot be undone.
            </p>
          </div>
        </div>
      </Modal>
    </div>
  );
};

const mapStateToProps = (state: any) => ({
  doors: state.doors.doorData,
  singleDoor: state.doors.singleDoor,
});

const mapDispatchToProps = {
  getAllDoors,
  setDoor,
  deleteDoor,
};

export default connect(mapStateToProps, mapDispatchToProps)(DoorList);




